import { useState } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { CurrentUserContextProvider, useCurrentUser } from 'context'
import { AvatarOverrideProvider } from 'context/useAvatarOverride'
import { useFeatureToggle } from 'hooks'
import { ArcTopBar } from 'components/ArcTopBar'
import { SidebarMenu } from 'components/SidebarMenu/SidebarMenu'
import { getSidebarUserId } from 'components/Layout/util'
import { ArcShellContext } from './ArcShellContext'

const ArcShellChrome = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isApp = searchParams.get('app') === 'true'
  const { currentUser } = useCurrentUser()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)

  return (
    <>
      <ArcTopBar
        hidden={isApp}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed(current => !current)}
      />
      {!isApp && (
        <SidebarMenu
          profile={currentUser && currentUser.profile_used}
          pathname={location.pathname}
          userID={getSidebarUserId(currentUser)}
          collapsed={sidebarCollapsed}
        />
      )}
      {children}
    </>
  )
}

/**
 * Pathless layout route (AR-578): mounts the Arc chrome ONCE around every
 * app route, so navigating between SPA routes re-renders — but never
 * remounts — the top bar and sidebar (collapsed state, school fetch and
 * widgets all survive). Pages keep rendering <Layout> as always; under Arc,
 * Layout sees ArcShellContext and renders only the content container.
 * With the flag off this component is a pass-through and the legacy
 * per-page chrome behaves exactly as before.
 */
export const ArcShellLayout = () => {
  const isArcEnabled = useFeatureToggle('arc-mosaico-project') as boolean

  if (!isArcEnabled) return <Outlet />

  return (
    <ArcShellContext.Provider value={true}>
      <CurrentUserContextProvider>
        <AvatarOverrideProvider>
          <ArcShellChrome>
            <Outlet />
          </ArcShellChrome>
        </AvatarOverrideProvider>
      </CurrentUserContextProvider>
    </ArcShellContext.Provider>
  )
}
